import type { Link } from '@app/app/package/base/cms/package/cms-domain';
import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import { type Lobby, TileType } from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { updateRobotsTag } from '../../_ui-core_/base/package/util/html-helper';
import { sessionStorage_getOrNull, sessionStorage_set } from '../../_ui-core_/base/package/util/sessionStorage';
import { showLogoutModal } from '../../_ui-core_/components/drawers/free-spin-logout-modal/show-logout-modal';
import { RichTextStyleType } from '../../_ui-core_/components/ui-rich-text/ui-rich-text';
import App from '../../app';
import { MainRoute } from '../../app/package/base/router/router';
import { TabNavigationType } from '../../app/package/base/router/router-types/router-types';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { LoginStatus } from '../../app/package/base/service/login/login-domain';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import style from './styles.css?inline';

const CName = 'sitemap-page';

type SitemapEntry = {
  name: string;
  removeNoFollow?: boolean;
  sameSite?: boolean;
  url: string;
  action?: () => void;
};

type SitemapSections = Record<string, SitemapEntry[]>;

@customElement(CName)
export class SitemapPage extends LitElement {
  static readonly styles = unsafeCSS(style);
  static readonly NUMBER_OF_GAMES_TO_SHOW = 2000;

  @property() headerTitle: string;
  @state() private _sitemapSlugs: string[] = [];
  @state() private _generalSections: SitemapSections = {};
  @state() private _gamesSections: SitemapSections = {};
  @state() private _loading = true;

  private _renderTaskId: number | null = null;
  private readonly _subHelper = new SubHelper();

  connectedCallback() {
    super.connectedCallback();
    this._renderTaskId = window.$app.renderService.addTask('sitemap');
    updateRobotsTag('none');
    this._loadPage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  protected firstUpdated() {
    this._updateHeader();
  }

  render() {
    const title = `<h1>${window.$app.appConfig.metaTitle} ${I18n.footer.links.sitemap}</h1>`;
    return html`
      <main class="${this._loading ? 'loading' : ''}">
        <ui-rich-text .content=${title} .styleType=${RichTextStyleType.PAGE}></ui-rich-text>
        <!-- General, Categories, Account, Providers -->
        <div class="section-wrapper">
          ${Object.entries(this._generalSections)
            .filter(([, items]) => items.length > 0)
            .map(([groupTitle, items]) => this._renderSection(groupTitle, items))}
        </div>
        <!-- Games -->
        <h2>${I18n.base.games}</h2>
        <div class="section-wrapper">
          ${Object.entries(this._gamesSections)
            .filter(([, items]) => items.length > 0)
            .map(([groupTitle, items]) => this._renderSection(groupTitle, items))}
        </div>
      </main>
    `;
  }

  private _renderSection(groupTitle: string, items: SitemapEntry[]) {
    const id = `section-${groupTitle.toLowerCase().replace(/\s+/g, '-')}`;
    return html`
      <section aria-labelledby=${id}>
        <h2 id=${id} class="section-title">${this._translateSectionTitle(groupTitle)}</h2>
        <div>
          <ul>
            ${repeat(
              items,
              (item) => item.name,
              (item) => {
                const isExternal = item.url.startsWith('http');
                const shouldOpenInNewTab = isExternal && item.sameSite === false;
                const href = isExternal ? item.url : `/${window.$app.appConfig.lang}/${item.url}`;
                return html`
                  <li>
                    <a
                      href=${href}
                      rel=${ifDefined(item.removeNoFollow ? undefined : 'nofollow')}
                      target=${shouldOpenInNewTab ? '_blank' : '_self'}
                      @click=${(ev: PointerEvent) => this._handleClick(ev, item)}
                    >
                      ${item.name}
                    </a>
                  </li>
                `;
              },
            )}
          </ul>
        </div>
      </section>
    `;
  }

  private _handleClick(ev: PointerEvent, item: SitemapEntry) {
    if (item.url.startsWith('http') && item.sameSite === false) return;
    ev.preventDefault();
    if (item.action) {
      item.action();
    } else {
      App.instance.router.navigateTo(item.url, TabNavigationType.SAME);
    }
  }

  private async _loadPage() {
    await this._loadSitemapSlugs();
    if (!this.isConnected) return;

    this._subHelper.addSub(App.instance.content.getLobby(), (lobby) => this._addProvidersAndTags(lobby), true);
    this._addGeneralRoutes();

    const tasks: Promise<any>[] = [];

    // Add all games
    tasks.push(
      App.instance.content
        .filterGames({ filterId: 'AllGames', isUserBased: false }, TileType.P1, SitemapPage.NUMBER_OF_GAMES_TO_SHOW)
        .then((games) => {
          if (!this.isConnected) return;
          const grouped: SitemapSections = {};

          games.forEach((game) => {
            const entry: SitemapEntry = {
              name: game.title,
              url: `${App.instance.featureFlag.gameInfoSlug}/${game.slug}`,
              removeNoFollow: this._sitemapSlugs.includes(game.slug),
            };

            const firstChar = game.title?.[0]?.toUpperCase() ?? '';
            const groupKey = /^[A-Z]$/.test(firstChar) ? firstChar : '#';

            grouped[groupKey] ??= [];
            grouped[groupKey].push(entry);
          });

          for (const key in grouped) {
            if (grouped[key]) grouped[key].sort((a, b) => a.name.localeCompare(b.name));
          }

          const sortedSections = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
          this._gamesSections = Object.fromEntries(sortedSections);
        })
        .catch((err) => {
          window.$app.logger.log('Sitemap filterGames error:', err);
        }),
    );

    // Add logged in routes
    tasks.push(
      (async () => {
        try {
          const status = await App.instance.login.getFinalLoginStatus();
          if (!this.isConnected) return;
          if (status === LoginStatus.LOGGED_IN) this._addLoggedInRoutes();
        } catch (e) {
          window.$app.logger.warn('Sitemap getFinalLoginStatus failed: ', e);
        }
      })(),
    );

    // Add static pages marked for sitemap
    tasks.push(
      App.instance.content
        .getStaticPages()
        .then((pages) => {
          if (!this.isConnected) return;

          // Collect URLs already present in the General section to avoid duplicates
          const existingUrls = new Set((this._generalSections.General ?? []).map((e) => e.url));

          const entries: SitemapEntry[] = pages
            .filter((page) => page.shouldBeAddedToSitemap)
            .map((page) => ({
              name: page.seo.metaTitle || page.footerLabel || page.path,
              url: page.path.replace(/^\//, ''),
              removeNoFollow: this._sitemapSlugs.includes(page.path),
            }))
            .filter((entry) => entry.name && !existingUrls.has(entry.url));

          if (entries.length > 0) {
            this._generalSections = {
              ...this._generalSections,
              General: [...(this._generalSections.General ?? []), ...entries].sort((a, b) =>
                a.name.localeCompare(b.name),
              ),
            };
          }
        })
        .catch((err) => {
          window.$app.logger.warn('Sitemap static pages error:', err);
        }),
    );

    try {
      await Promise.all(tasks);
    } finally {
      if (this._renderTaskId !== null) {
        window.$app.renderService.removeTask(this._renderTaskId);
      }
      this._loading = false;
    }
  }

  private _updateHeader() {
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: this.headerTitle,
      trackingSource: TrackingEventSource.SITEMAP,
      isGoBackShown: true,
    });
  }

  private _addProvidersAndTags(lobby?: Lobby) {
    if (!lobby) return;

    const categories: SitemapEntry[] = (lobby.searchTags ?? []).map((tag) => ({
      name: tag.name,
      url: `${MainRoute.TAG}/${tag.slug}`,
      removeNoFollow: this._sitemapSlugs.includes(tag.slug),
    }));

    this._generalSections = { ...this._generalSections, Categories: categories };

    const providers: SitemapEntry[] = lobby.studios
      .map((studio) => ({
        name: studio.name,
        url: `${MainRoute.PROVIDER}/${studio.slug}`,
        removeNoFollow: this._sitemapSlugs.includes(studio.slug),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this._generalSections = { ...this._generalSections, Providers: providers };
  }

  private _addGeneralRoutes() {
    const isSh = App.instance.appSettings.isSHCasino;
    const promotionsSlug = App.instance.featureFlag.promotionsSlug;
    const helpCenterEntry: SitemapEntry = isSh
      ? { name: I18n.sh.navigation.accountMenu.helpCenter, url: MainRoute.CONTACT_US }
      : { name: I18n.navigation.accountMenu.helpCenter, url: MainRoute.HELP_CENTER };

    const config = window.$app.casinoConfig;
    const tacFooterLink = config.footer?.links.find((l: Link) => l.title === 'footer.links.termsAndConditions');
    const tacEntry: SitemapEntry | undefined = tacFooterLink
      ? { name: I18n.footer.links.termsAndConditions, url: tacFooterLink.nav, sameSite: tacFooterLink.sameSite }
      : undefined;

    if (!tacEntry) {
      window.$app.logger.warn(`[${CName}] TAC footer link not found in casino config`);
    }

    const links: SitemapEntry[] = [
      ...(isSh ? [] : [{ name: 'Club1000', url: MainRoute.CLUB1000 }]),
      { name: I18n.base.login, url: MainRoute.LOGIN, action: () => App.instance.product.gotoLogin('sitemap') },
      { name: I18n.base.providers, url: MainRoute.PROVIDER },
      { name: I18n.footer.links.aboutUs, url: MainRoute.ABOUT_US },
      { name: I18n.footer.links.accessibility, url: MainRoute.A11Y },
      { name: I18n.footer.links.gamesInfo, url: MainRoute.GAMES_INFO },
      { name: I18n.footer.links.imprint, url: MainRoute.IMPRINT },
      { name: I18n.footer.links.offerConditions, url: MainRoute.TERMS_OF_OFFER },
      ...(tacEntry ? [tacEntry] : []),
      { name: I18n.mod.bonus.promotions, url: promotionsSlug },
      helpCenterEntry,
      { name: I18n.navigation.navbar.search, url: MainRoute.SEARCH },
    ]
      .map((link) => ({ ...link, removeNoFollow: this._sitemapSlugs.includes(link.url) }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this._generalSections = { General: links, ...this._generalSections };
  }

  private _addLoggedInRoutes() {
    const links: SitemapEntry[] = [
      { name: I18n.base.account, url: MainRoute.ACCOUNT },
      { name: I18n.navigation.accountMenu.limits, url: MainRoute.LIMITS },
      {
        name: I18n.navigation.accountMenu.logout,
        url: MainRoute.LOGOUT,
        action: () => {
          showLogoutModal();
        },
      },
    ]
      .map((link) => ({ ...link, removeNoFollow: false }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this._generalSections = { ...this._generalSections, Account: links };
  }

  private async _loadSitemapSlugs() {
    // Get from session storage if available
    const cachedSitemapSlugs = sessionStorage_getOrNull<string[]>('sitemapSlugs');
    if (cachedSitemapSlugs !== null) {
      // Already loaded, no need to fetch again
      this._sitemapSlugs = cachedSitemapSlugs;
      return;
    }

    const parser = new DOMParser();
    const xmlPath = 'https://games.tipico.de/sitemap.xml';

    try {
      const response = await fetch(xmlPath);
      const xmlText = await response.text();
      const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
      const urls = xmlDoc.getElementsByTagName('loc');

      for (const element of urls) {
        if (!element.textContent) continue;
        const path = new URL(element.textContent).pathname;
        const segments = path.split('/').filter(Boolean);
        const slug = segments.at(-1);
        if (slug) this._sitemapSlugs.push(slug);
      }

      // Cache in session storage
      sessionStorage_set('sitemapSlugs', this._sitemapSlugs);
    } catch (err) {
      window.$app.logger.warn('Sitemap fetching XML error:', err);
    }
  }

  private _translateSectionTitle(title: string): string {
    switch (title) {
      case 'General':
        return I18n.base.general;
      case 'Account':
        return I18n.base.account;
      case 'Categories':
        return I18n.base.categories;
      case 'Providers':
        return I18n.base.providers;
      default:
        return title;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: SitemapPage;
  }
}
